import { AuthScope, AuthPermission, PersonaType, CrewMember } from '@bechtel/types';
import { concat, some } from 'lodash';
import { useMemo } from 'react';
import { useAppUser, CrewMemberExt } from '@bechtel/client-common';

export interface CrewChangePermissions {
  canSelectSuperintendent: boolean;
  canSelectGeneralForeman: boolean;
  canCreateRequest: boolean;
  canApproveAllRequest: boolean;
  canCancelRequest: (requestCreator: CrewMember) => boolean;
  canApproveDeclineRequest: (
    crewChangeSourceMembers: CrewMember[] | CrewMemberExt[],
    crewChangeDestMembers: CrewMember[] | CrewMemberExt[],
    requestCreator: CrewMember
  ) => boolean;
  canViewCrewChange: boolean;
  isUserPartOfCrew: (crewChangeSourceDestMembers: CrewMember[] | CrewMemberExt[]) => boolean;
}

export function useCrewChangePermissions(): CrewChangePermissions {
  const { hasPermissionAndScope, user } = useAppUser();
  const userCrewMember = user?.crewMember;
  const crewChangePermissions = useMemo(() => {
    const canCreateRequest =
      hasPermissionAndScope(AuthPermission.ADD_EDIT_CREW_CHANGE, AuthScope.ANY_CREW_ANY_HIERARCHY) ||
      hasPermissionAndScope(AuthPermission.ADD_EDIT_CREW_CHANGE, AuthScope.MY_CREW_ANY_HIERARCHY) ||
      hasPermissionAndScope(AuthPermission.ADD_EDIT_CREW_CHANGE, AuthScope.MY_CREW_MY_HIERARCHY);

    const canSelectPersona = (persona: PersonaType) => {
      return (
        hasPermissionAndScope(AuthPermission.ADD_EDIT_CREW_CHANGE, AuthScope.ANY_CREW_ANY_HIERARCHY) ||
        (canCreateRequest && userCrewMember?.persona === persona)
      );
    };
    const canSelectSuperintendent = canSelectPersona(PersonaType.SUPERINTENDENT);
    let canSelectGeneralForeman = canSelectPersona(PersonaType.GENERAL_FOREMAN);
    if (canSelectSuperintendent) canSelectGeneralForeman = true;
    const canCancelRequest = (requestCreator: CrewMember) => {
      return (
        requestCreator.id === userCrewMember?.id &&
        hasPermissionAndScope(AuthPermission.CANCEL_CREW_CHANGE, AuthScope.MY_REQUEST)
      );
    };
    const canApproveDeclineRequest = (
      crewChangeSourceMembers: CrewMember[] | CrewMemberExt[],
      crewChangeDestMembers: CrewMember[] | CrewMemberExt[],
      requestCreator: CrewMember
    ) => {
      const crewChangeSourceDestMembers = concat(crewChangeSourceMembers, crewChangeDestMembers);
      const hasMyCrewApprorveDecline =
        hasPermissionAndScope(AuthPermission.APPROVE_DECLINE_CREW_CHANGE, AuthScope.MY_CREW) &&
        some(crewChangeSourceDestMembers, (item: CrewMemberExt) => {
          return item.id === userCrewMember?.id;
        });

      if (
        !some(crewChangeSourceDestMembers, (item: CrewMemberExt) => {
          return item.id === requestCreator.id;
        })
      ) {
        return (
          hasMyCrewApprorveDecline &&
          some(crewChangeDestMembers, (item: CrewMemberExt) => {
            return item.id === userCrewMember?.id;
          })
        );
      } else {
        return (
          requestCreator.id !== userCrewMember?.id &&
          (hasMyCrewApprorveDecline ||
            hasPermissionAndScope(AuthPermission.APPROVE_DECLINE_CREW_CHANGE, AuthScope.ALL_CREW))
        );
      }
    };

    // crewChangeMembers - combined array of original and destination members
    const isUserPartOfCrew = (crewChangeSourceDestMembers: CrewMember[] | CrewMemberExt[]) => {
      return (
        hasPermissionAndScope(AuthPermission.ADD_EDIT_CREW_CHANGE, AuthScope.ANY_CREW_ANY_HIERARCHY) ||
        ((hasPermissionAndScope(AuthPermission.ADD_EDIT_CREW_CHANGE, AuthScope.MY_CREW_MY_HIERARCHY) ||
          hasPermissionAndScope(AuthPermission.ADD_EDIT_CREW_CHANGE, AuthScope.MY_CREW_ANY_HIERARCHY)) &&
          some(crewChangeSourceDestMembers, (item: CrewMemberExt) => {
            return item.id === userCrewMember?.id;
          }))
      );
    };

    const canViewCrewChange = hasPermissionAndScope(AuthPermission.VIEW_CREW_CHANGE_MODULE, AuthScope.APPLICATION);
    const canApproveAllRequest = hasPermissionAndScope(AuthPermission.APPROVE_DECLINE_CREW_CHANGE, AuthScope.ALL_CREW);

    return {
      canSelectSuperintendent,
      canSelectGeneralForeman,
      canCreateRequest,
      canCancelRequest,
      canApproveDeclineRequest,
      canViewCrewChange,
      isUserPartOfCrew,
      canApproveAllRequest,
    };
  }, [hasPermissionAndScope, userCrewMember]);

  return crewChangePermissions;
}
